import { useEffect, useState } from "react";
import { ContentLayout } from "@/components/layout/ContentLayout";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, MoveUp, MoveDown, ImageIcon } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Link } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { deleteFileFromS3 } from "@/services/s3Service";

const languageFlags = {
  tr: "🇹🇷",
  en: "🇬🇧",
  ar: "🇸🇦"
};

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState("tr");
  const { toast } = useToast();

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line
  }, []);

  async function fetchProjects() {
    setLoading(true);
    const { data, error } = await supabase
      .from("home_projects")
      .select("id, title, order_num, media_count")
      .order("order_num", { ascending: true });
    if (error) {
      toast({ title: "Hata", description: "Projeler yüklenemedi.", variant: "destructive" });
    } else {
      // JSON verilerini parse et
      const parsedProjects = (data || []).map(project => ({
        ...project,
        title: typeof project.title === 'string' ? JSON.parse(project.title) : project.title
      }));
      setProjects(parsedProjects);
    }
    setLoading(false);
  }

  const moveProject = async (id, direction) => {
    const index = projects.findIndex((project) => project.id === id);
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === projects.length - 1)
    ) {
      return;
    }
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    const updatedProjects = [...projects];
    [updatedProjects[index], updatedProjects[swapIndex]] = [updatedProjects[swapIndex], updatedProjects[index]];
    // Sıralama numaralarını güncelle
    for (let i = 0; i < updatedProjects.length; i++) {
      updatedProjects[i].order_num = i + 1;
      await supabase.from("home_projects").update({ order_num: i + 1 }).eq("id", updatedProjects[i].id);
    }
    setProjects([...updatedProjects]);
  };

  const deleteProject = async (id) => {
    try {
      // Önce proje bilgilerini al
      const { data: projectData, error: fetchError } = await supabase
        .from("home_projects")
        .select("image_url")
        .eq("id", id)
        .single();
        
      if (fetchError) {
        console.error("Proje bilgileri alınamadı:", fetchError);
        toast({ title: "Hata", description: "Proje bilgileri alınamadı.", variant: "destructive" });
        return;
      }
      
      // Projeyi veritabanından sil
      const { error } = await supabase
        .from("home_projects")
        .delete()
        .eq("id", id);
        
      if (error) {
        toast({ title: "Hata", description: "Proje silinemedi.", variant: "destructive" });
        return;
      }
      
      // S3'den resmi sil
      if (projectData?.image_url) {
        try {
          await deleteFileFromS3(projectData.image_url);
        } catch (s3Error) {
          console.error("S3 dosya silme hatası:", s3Error);
          // S3 silme hatası olsa bile işleme devam et
        }
      }
      
      setProjects(projects.filter((project) => project.id !== id));
      toast({ title: "Başarılı", description: "Proje ve ilişkili dosyalar silindi." });
    } catch (error) {
      console.error("Proje silme hatası:", error);
      toast({ title: "Hata", description: "Proje silinirken bir sorun oluştu.", variant: "destructive" });
    }
  };

  return (
    <ContentLayout
      title="Anasayfa Projeleri"
      backLink="/dashboard"
      actionButton={{ label: "Yeni Proje Ekle", href: "/content/home/projects/new" }}
    >
      <div className="mb-4">
        <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Dil seçin" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="tr">{languageFlags.tr} Türkçe</SelectItem>
            <SelectItem value="en">{languageFlags.en} English</SelectItem>
            <SelectItem value="ar">{languageFlags.ar} العربية</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <ScrollArea className="h-full max-h-[600px]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">Sıra</TableHead>
              <TableHead>Başlık</TableHead>
              <TableHead>Medya</TableHead>
              <TableHead className="w-[150px]">İşlemler</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4}>Projeler yükleniyor...</TableCell></TableRow>
            ) : projects.length === 0 ? (
              <TableRow><TableCell colSpan={4}>Hiç proje yok.</TableCell></TableRow>
            ) : (
              projects.map((project) => (
                <TableRow key={project.id}>
                  <TableCell>{project.order_num}</TableCell>
                  <TableCell className="font-medium">{project.title?.[selectedLanguage] || ''}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="flex items-center gap-1">
                      <ImageIcon className="h-3 w-3" />
                      {project.media_count ?? 0}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="icon" onClick={() => moveProject(project.id, "up")}> <MoveUp className="h-4 w-4" /> </Button>
                      <Button variant="ghost" size="icon" onClick={() => moveProject(project.id, "down")}> <MoveDown className="h-4 w-4" /> </Button>
                      <Button variant="ghost" size="icon" asChild>
                        <Link to={`/content/home/projects/${project.id}/edit`}><Pencil className="h-4 w-4" /></Link>
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteProject(project.id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </ContentLayout>
  );
}
